import type { Json } from "@/lib/database.types";
import type {
  AdminAiAbuseReview,
  AdminAppLog,
  AdminBanHistoryEntry,
  AdminCreditHistoryEntry,
  AdminDeleteAccountResult,
  AdminLogsPage,
  AdminPqrListItem,
  AdminUserDetail,
  AdminUserDirectoryPage,
  AdminUserPqrHistoryEntry,
  AdminUserSummary,
  AcknowledgeAppLogResult,
  BanUserResult,
  CreateTestAccountResult,
  AdminTierUpdateResult,
  UnbanUserResult,
} from "@/features/admin/admin.types";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function asNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asNullableNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function asJson(value: unknown): Json {
  if (
    value === null
    || typeof value === "string"
    || typeof value === "number"
    || typeof value === "boolean"
    || Array.isArray(value)
    || typeof value === "object"
  ) {
    return value as Json;
  }

  return null;
}

function asArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function asAiAbuseReviewStatus(value: unknown): AdminAiAbuseReview["status"] | null {
  return value === "open" || value === "acknowledged" || value === "cleared"
    ? value
    : null;
}

function asQuotaResourceKind(value: unknown): AdminAppLog["quotaResourceKind"] {
  return value === "database_bandwidth"
    || value === "edge_invocations"
    || value === "monthly_ai_budget"
    || value === "storage"
    ? value
    : null;
}

export function mapAdminUserSummary(rawValue: unknown): AdminUserSummary {
  const value = asRecord(rawValue);
  const status = asString(value.status, "active");
  return {
    billingStatus: asString(value.billing_status, "active"),
    creditBalance: asNumber(value.credit_balance),
    deletedAt: asNullableString(value.deleted_at),
    deletionScheduledAt: asNullableString(value.deletion_scheduled_at),
    email: asString(value.email),
    lastLoginAt: asNullableString(value.last_login_at),
    name: asString(value.name),
    registeredAt: asString(value.registered_at),
    status: status === "banned" || status === "pending_deletion" || status === "deleted"
      ? status
      : "active",
    testAccount: asBoolean(value.test_account),
    tier: (asString(value.tier, "free") as AdminUserSummary["tier"]),
    userId: asString(value.user_id),
  };
}

export function mapAdminUsersPage(rawValue: unknown): AdminUserDirectoryPage {
  const value = asRecord(rawValue);
  return {
    items: asArray(value.items).map((item) => mapAdminUserSummary(item)),
    page: Math.max(1, asNumber(value.page, 1)),
    pageCount: Math.max(1, asNumber(value.page_count, 1)),
    totalCount: Math.max(0, asNumber(value.total_count)),
  };
}

export function mapAdminCreditHistoryEntry(rawValue: unknown): AdminCreditHistoryEntry {
  const value = asRecord(rawValue);
  return {
    amount: asNumber(value.amount),
    createdAt: asString(value.created_at),
    feature: asString(value.feature),
    id: asString(value.id),
    metadata: asJson(value.metadata),
  };
}

export function mapAdminBanHistoryEntry(rawValue: unknown): AdminBanHistoryEntry {
  const value = asRecord(rawValue);
  return {
    bannedAt: asString(value.banned_at),
    bannedBy: asString(value.banned_by),
    expiresAt: asNullableString(value.expires_at),
    id: asString(value.id),
    liftReason: asNullableString(value.lift_reason),
    liftedAt: asNullableString(value.lifted_at),
    liftedBy: asNullableString(value.lifted_by),
    reason: asString(value.reason),
  };
}

export function mapAdminUserPqrHistoryEntry(rawValue: unknown): AdminUserPqrHistoryEntry {
  const value = asRecord(rawValue);
  return {
    createdAt: asString(value.created_at),
    id: asString(value.id),
    status: asString(value.status),
    subject: asString(value.subject),
    ticketRef: asString(value.ticket_ref),
  };
}

export function mapAdminUserDetail(rawValue: unknown): AdminUserDetail {
  const value = asRecord(rawValue);
  return {
    activeBanExpiresAt: asNullableString(value.active_ban_expires_at),
    banHistory: asArray(value.ban_history).map((item) => mapAdminBanHistoryEntry(item)),
    billingStatus: asString(value.billing_status, "active"),
    creditBalance: asNumber(value.credit_balance),
    creditTransactions: asArray(value.credit_transactions).map((item) => mapAdminCreditHistoryEntry(item)),
    deletedAt: asNullableString(value.deleted_at),
    deletionRequestedAt: asNullableString(value.deletion_requested_at),
    deletionScheduledAt: asNullableString(value.deletion_scheduled_at),
    email: asString(value.email),
    firstName: asString(value.first_name),
    isAdmin: asBoolean(value.is_admin),
    languagePref: (asString(value.language_pref, "en") as AdminUserDetail["languagePref"]),
    lastLoginAt: asNullableString(value.last_login_at),
    lastName: asString(value.last_name),
    phone: asNullableString(value.phone),
    pqrSubmissions: asArray(value.pqr_submissions).map((item) => mapAdminUserPqrHistoryEntry(item)),
    registeredAt: asString(value.registered_at),
    testAccount: asBoolean(value.test_account),
    tier: (asString(value.tier, "free") as AdminUserDetail["tier"]),
    userId: asString(value.user_id),
  };
}

export function mapAdminDeleteAccountResult(rawValue: unknown): AdminDeleteAccountResult {
  const value = asRecord(rawValue);
  return {
    deletedAt: asString(value.deletedAt),
    status: "deleted",
    userId: asString(value.userId),
  };
}

export function mapCreateTestAccountResult(rawValue: unknown): CreateTestAccountResult {
  const value = asRecord(rawValue);
  return {
    email: asString(value.email),
    testAccount: asBoolean(value.testAccount),
    tier: asString(value.tier, "free") as CreateTestAccountResult["tier"],
    userId: asString(value.userId),
  };
}

export function mapBanUserResult(rawValue: unknown): BanUserResult {
  const value = asRecord(rawValue);
  return {
    expiresAt: asNullableString(value.expiresAt),
    status: "banned",
    userId: asString(value.userId),
  };
}

export function mapUnbanUserResult(rawValue: unknown): UnbanUserResult {
  const value = asRecord(rawValue);
  return {
    status: "active",
    userId: asString(value.userId),
  };
}

export function mapAdminPqrListItem(rawValue: unknown): AdminPqrListItem {
  const value = asRecord(rawValue);
  return {
    category: asString(value.category) as AdminPqrListItem["category"],
    createdAt: asString(value.created_at),
    email: asString(value.email),
    id: asString(value.id),
    name: asString(value.name),
    status: asString(value.status) as AdminPqrListItem["status"],
    subject: asString(value.subject),
    ticketRef: asString(value.ticket_ref),
    userId: asNullableString(value.user_id),
  };
}

export function mapAdminTierUpdateResult(rawValue: unknown): AdminTierUpdateResult {
  const value = asRecord(rawValue);
  return {
    nextTier: asString(value.nextTier, "free") as AdminTierUpdateResult["nextTier"],
    previousTier: asString(value.previousTier, "free") as AdminTierUpdateResult["previousTier"],
    projectsLimit:
      typeof value.projectsLimit === "number" && Number.isFinite(value.projectsLimit)
        ? value.projectsLimit
        : null,
    projectsUsed:
      typeof value.projectsUsed === "number" && Number.isFinite(value.projectsUsed)
        ? value.projectsUsed
        : null,
    requiresProjectSelection: asBoolean(value.requiresProjectSelection),
    userId: asString(value.userId),
  };
}

export function mapAdminAiAbuseReview(rawValue: unknown): AdminAiAbuseReview {
  const value = asRecord(rawValue);
  return {
    acknowledgedAt: asNullableString(value.acknowledged_at),
    clearedAt: asNullableString(value.cleared_at),
    detectedAt: asString(value.detected_at),
    id: asString(value.id),
    notes: asNullableString(value.notes),
    observed24hRequests: asNumber(value.observed_24h_requests),
    rateLimitFactor: asNumber(value.rate_limit_factor, 0.5),
    status: asAiAbuseReviewStatus(value.status) ?? "open",
    tierAverage24hRequests: asNumber(value.tier_average_24h_requests),
    tierSnapshot: asString(value.tier_snapshot, "free") as AdminAiAbuseReview["tierSnapshot"],
    usageMultiplier: asNumber(value.usage_multiplier),
    userId: asString(value.user_id),
  };
}

export function mapAdminAppLog(rawValue: unknown): AdminAppLog {
  const value = asRecord(rawValue);
  const metadata = asRecord(value.metadata);
  return {
    acknowledged: asBoolean(value.acknowledged),
    acknowledgedBy: asNullableString(value.acknowledged_by),
    alertStage: asNullableString(metadata.alertStage),
    createdAt: asString(value.created_at),
    flaggedUserId: asNullableString(metadata.userId),
    id: asString(value.id),
    message: asString(value.message),
    metadata: asJson(value.metadata),
    quotaObservedPercent: asNullableNumber(metadata.observedPercent),
    quotaResourceKind: asQuotaResourceKind(metadata.resourceKind),
    quotaThresholdPercent: asNullableNumber(metadata.thresholdPercent),
    reviewStatus: asAiAbuseReviewStatus(metadata.reviewStatus ?? metadata.status),
    severity: asString(value.severity, "info") as AdminAppLog["severity"],
    source: asString(value.source),
    usageMultiplier: asNullableNumber(metadata.usageMultiplier),
  };
}

export function mapAdminLogsPage(input: {
  count: number | null;
  data: unknown[];
  page: number;
  pageSize: number;
}): AdminLogsPage {
  const totalCount = Math.max(0, input.count ?? 0);
  return {
    items: input.data.map((item) => mapAdminAppLog(item)),
    page: input.page,
    pageCount: Math.max(1, Math.ceil(totalCount / input.pageSize)),
    totalCount,
  };
}

export function mapAcknowledgeAppLogResult(rawValue: unknown): AcknowledgeAppLogResult {
  const value = asRecord(rawValue);
  return {
    acknowledged: asBoolean(value.acknowledged),
    acknowledgedBy: asNullableString(value.acknowledgedBy),
    logId: asString(value.logId),
  };
}
